import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Dialog,
  DialogContent,
} from '@mui/material';
import { SettingsCard } from '../../components/composed';
import { Button } from '../../components/primitives';
import { ApprovalBanner, AreYouSure } from '../../components/domain/object_management';
import Events from '../Events/Events';
import { getCsrfToken } from '../../utils/csrf';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const ResourceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRemoved, setIsRemoved] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);

  useEffect(() => {
    if (id) {
      fetchResource(id);
    }
  }, [id]);

  const fetchResource = async (resourceId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/resources/${resourceId}/`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Resource not found');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResource(data);
    } catch (err) {
      console.error('Error fetching resource:', err);
      setError(err.message || 'Failed to load resource. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (resource && resource.facility) {
      navigate(`/facilities/${resource.facility}`);
    } else {
      navigate('/facilities');
    }
  };

  const handleApprove = async () => {
    try {
      setUpdating(true);
      const csrfToken = getCsrfToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      const response = await fetch(`${API_BASE_URL}/resources/${id}/`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify({ is_approved: true, is_active: true }),
      });

      if (!response.ok) {
        throw new Error('Failed to approve resource');
      }

      const data = await response.json();
      // Ensure is_approved and is_active are set to true in the state
      setResource({ ...data, is_approved: true, is_active: true });
    } catch (err) {
      console.error('Error approving resource:', err);
      setError('Failed to approve resource. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeny = () => {
    setDenyDialogOpen(true);
  };

  const handleDenyCancel = () => {
    setDenyDialogOpen(false);
  };

  const handleDenyConfirm = async () => {
    try {
      setUpdating(true);
      const csrfToken = getCsrfToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      const response = await fetch(`${API_BASE_URL}/resources/${id}/`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify({ is_active: false }),
      });

      if (!response.ok) {
        throw new Error('Failed to deny resource');
      }

      setDenyDialogOpen(false);
      setIsRemoved(true);
    } catch (err) {
      console.error('Error denying resource:', err);
      setError('Failed to deny resource. Please try again.');
      setDenyDialogOpen(false);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'background.default',
          padding: 1,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <SettingsCard title="Resource Details" sx={{ mb: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          </SettingsCard>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'background.default',
          padding: 1,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <SettingsCard title="Resource Details" sx={{ mb: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
              <Button variant="outlined" onClick={handleBack}>
                Back to Facility
              </Button>
            </Box>
          </SettingsCard>
        </Box>
      </Box>
    );
  }

  if (!resource && !isRemoved) {
    return null;
  }

  // Show removed message if resource was denied
  if (isRemoved) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'background.default',
          padding: 1,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <SettingsCard title="Resource Details" sx={{ mb: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                This resource has been removed.
              </Typography>
              <Button variant="outlined" onClick={handleBack}>
                Back to Facility
              </Button>
            </Box>
          </SettingsCard>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        padding: 1,
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <SettingsCard title={resource.name || "Resource Details"} sx={{ mb: 0 }} onEdit={() => {}}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Approval Banner - only shown for unverified resources */}
            {!resource.is_approved && (
              <ApprovalBanner
                message="This resource needs review."
                onApprove={handleApprove}
                onDeny={handleDeny}
                disabled={updating}
              />
            )}

            <Box>
              <Button variant="outlined" onClick={handleBack} sx={{ mb: 3 }}>
                Back to Facility
              </Button>
            </Box>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Resource Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {resource.name || 'N/A'}
                    </Typography>
                  </Box>

                  {resource.schedule_url && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Schedule URL
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        <a href={resource.schedule_url} target="_blank" rel="noopener noreferrer">
                          {resource.schedule_url}
                        </a>
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {resource.facility && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Facility
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        <Button
                          variant="text"
                          onClick={() => navigate(`/facilities/${resource.facility}`)}
                        >
                          View Facility
                        </Button>
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </SettingsCard>

        {/* Events Section */}
        <Events resourceId={resource.id} />
      </Box>

      {/* Deny Confirmation Dialog */}
      <Dialog open={denyDialogOpen} onClose={handleDenyCancel} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 0 }}>
          <AreYouSure
            message="Are you sure you want to deny this item?"
            onDelete={handleDenyConfirm}
            onCancel={handleDenyCancel}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ResourceDetail;

